/**
 * CLI Property Management
 *
 * List properties and their linked GitHub repos.
 */

import process from 'node:process';
import { resolveConfig, hasFlag, getArgValue } from './auth.js';
import { fetchProperties, apiRequest, getApplicationArg } from './cli-helpers.js';
import { runPropertySkill } from './property-skill.js';
import { runPageSkill } from './page-skill.js';
import { runRecipe } from './recipe.js';

function pad(s: string, n: number): string {
  return s.padEnd(n);
}

function printHelp(): void {
  console.log(
    [
      'canary application: Manage applications (alias: canary property)',
      '',
      'Sub-commands:',
      '  list                              List all applications',
      '  create [options]                  Create a new application',
      '  update <name|id> [options]        Update application settings',
      '  repo link [options]               Link a GitHub repo to an application',
      '  repo update [options]             Update an existing repo link',
      '  repo unlink [options]             Unlink a GitHub repo from an application',
      '  skill <action> [options]          Manage the application\'s app skill (run `canary application skill help` for details)',
      '  page-skill <action> [options]     Review the application\'s page skills (run `canary application page-skill help` for details)',
      '  recipe <action> [options]         Provision + audit the application\'s interaction recipes (run `canary application recipe help` for details)',
      '',
      'Create options:',
      '  --name <name>                     Application name (required)',
      '  --base-url <url>                  Application base URL (required)',
      '  --description <text>              Optional description',
      '  --agent-context <text>            Optional agent context (max 8000 chars)',
      '  --env-var <NAME[:sensitive]>      Declare a per-environment variable (repeatable); values set per-environment, injected as $var.ENV.<NAME>',
      '  --login-path </path>              Relative login page path appended to the environment URL (e.g. /login)',
      '',
      'Update options:',
      '  --login-path </path>              Set the relative login page path (empty string clears it)',
      '  --description <text>              Set the description',
      '  --agent-context <text>            Set the agent context',
      '',
      'Repo link/update/unlink options:',
      '  --application <app_xxx|uuid>      Application ID (required; alias: --property)',
      '  --repo <owner/name>               Repo full name (required for link/unlink)',
      '  --repo-id <uuid>                  Repo link UUID (required for update)',
      '  --role <frontend|backend|...>     Conventional role (free-form)',
      '  --root-dir <path>                 Monorepo subdirectory hint',
      '  --agent-hint <text>               Free-form hint for the planner agent',
      '',
      'Common options:',
      '  --json                            Output as JSON',
    ].join('\n')
  );
}

type GitHubRepo = {
  id: string;
  repoFullName: string;
  repoName: string;
  defaultBranch: string;
  rootDir: string | null;
  role: string | null;
};

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const properties = await fetchProperties(apiUrl, token);

  if (json) {
    console.log(JSON.stringify(properties, null, 2));
    return;
  }

  if (properties.length === 0) {
    console.log('No applications found.');
    return;
  }

  console.log(`${pad('Name', 30)} ${pad('Base URL', 40)} ID`);
  for (const p of properties) {
    console.log(`${pad(p.name, 30)} ${pad(p.baseUrl, 40)} ${p.id}`);
  }

  // Show linked repos for each property
  console.log('');
  for (const p of properties) {
    const reposRes = await apiRequest<{ ok: boolean; items: GitHubRepo[] }>(
      apiUrl, token, 'GET', `/org/properties/${p.id}/github-repos`
    );
    const repos = reposRes.ok ? (reposRes.items ?? []) : [];
    if (repos.length > 0) {
      console.log(`${p.name} repos:`);
      for (const r of repos) {
        const dir = r.rootDir && r.rootDir !== '/' ? ` (${r.rootDir})` : '';
        const role = r.role ? ` [${r.role}]` : '';
        console.log(`  ${r.repoFullName}:${r.defaultBranch}${role}${dir}`);
      }
    }
  }
}

/**
 * Parse repeatable `--env-var NAME[:sensitive]` flags into env-var definitions.
 * Examples: `--env-var API_URL`, `--env-var API_TOKEN:sensitive`.
 */
function parseEnvVarDefinitions(
  argv: string[]
): Array<{ name: string; sensitive?: boolean }> {
  const defs: Array<{ name: string; sensitive?: boolean }> = [];
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] !== '--env-var') continue;
    const raw = argv[i + 1];
    if (!raw || raw.startsWith('-')) continue;
    const [name, modifier] = raw.split(':');
    if (!name) continue;
    // Accept `NAME` or `NAME:sensitive`; ignore-with-no-effect any other suffix.
    defs.push({ name, ...(modifier === 'sensitive' ? { sensitive: true } : {}) });
  }
  return defs;
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const name = getArgValue(argv, '--name');
  const baseUrl = getArgValue(argv, '--base-url');
  const description = getArgValue(argv, '--description');
  const agentContext = getArgValue(argv, '--agent-context');
  const loginPath = getArgValue(argv, '--login-path');
  const envVarDefinitions = parseEnvVarDefinitions(argv);

  if (!name || !baseUrl) {
    console.error('Error: --name and --base-url are required.');
    console.error('Usage: canary application create --name <name> --base-url <url>');
    process.exit(1);
  }

  const body: Record<string, unknown> = {
    name,
    defaultEnvironment: { name: 'production', url: baseUrl },
  };
  if (description !== undefined) body.description = description;
  if (agentContext !== undefined) body.agentContext = agentContext;
  if (loginPath !== undefined) body.loginPath = loginPath;
  if (envVarDefinitions.length > 0) body.envVarDefinitions = envVarDefinitions;

  const result = await apiRequest<{ ok: boolean; id?: string; error?: string }>(
    apiUrl,
    token,
    'POST',
    '/org/properties',
    body
  );

  if (!result.ok) {
    if (json) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
    }
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`Created application "${name}" (${result.id})`);
}

async function handleUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const appArg = argv.find((a) => !a.startsWith('-')) ?? getApplicationArg(argv);
  const loginPath = getArgValue(argv, '--login-path');
  const description = getArgValue(argv, '--description');
  const agentContext = getArgValue(argv, '--agent-context');

  if (!appArg) {
    console.error('Error: Missing application name or ID.');
    console.error('Usage: canary application update <name|id> [--login-path </path>] [--description <text>]');
    process.exit(1);
  }
  if (loginPath === undefined && description === undefined && agentContext === undefined) {
    console.error(
      'Error: No fields to update. Provide at least one of: --login-path, --description, --agent-context'
    );
    process.exit(1);
  }

  const properties = await fetchProperties(apiUrl, token);
  const property = properties.find(
    (p) => p.id === appArg || p.name.toLowerCase() === appArg.toLowerCase()
  );
  if (!property) {
    console.error(`Error: Application "${appArg}" not found.`);
    if (properties.length > 0) {
      console.error('Available applications:');
      for (const p of properties) console.error(`  - ${p.name} (${p.id})`);
    }
    process.exit(1);
  }

  const body: Record<string, unknown> = {};
  // Empty string clears the login path (sessions start at the environment URL).
  if (loginPath !== undefined) body.loginPath = loginPath;
  if (description !== undefined) body.description = description;
  if (agentContext !== undefined) body.agentContext = agentContext;

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'PUT',
    `/org/properties/${property.id}`,
    body
  );

  if (!result.ok) {
    if (json) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
    }
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify({ ok: true, applicationId: property.id, updated: body }, null, 2));
    return;
  }

  console.log(`Updated application "${property.name}" (${property.id}):`);
  if (loginPath !== undefined) {
    console.log(`  loginPath: -> ${loginPath || '(cleared — sessions start at the environment URL)'}`);
  }
  if (description !== undefined) console.log('  description: updated');
  if (agentContext !== undefined) console.log('  agentContext: updated');
}

async function handleRepoLink(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = getApplicationArg(argv);
  const repo = getArgValue(argv, '--repo');
  const role = getArgValue(argv, '--role');
  const rootDir = getArgValue(argv, '--root-dir');
  const agentHint = getArgValue(argv, '--agent-hint');

  if (!propertyId || !repo) {
    console.error('Error: --application and --repo are required.');
    process.exit(1);
  }

  const body: Record<string, unknown> = { repoFullName: repo };
  if (role !== undefined) body.role = role;
  if (rootDir !== undefined) body.rootDir = rootDir;
  if (agentHint !== undefined) body.agentHint = agentHint;

  const result = await apiRequest<{ ok: boolean; id?: string; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/github-repos`,
    body
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) console.log(JSON.stringify(result, null, 2));
  else console.log(`Linked ${repo} to application ${propertyId} (${result.id})`);
}

async function handleRepoUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = getApplicationArg(argv);
  const repoId = getArgValue(argv, '--repo-id');
  const role = getArgValue(argv, '--role');
  const rootDir = getArgValue(argv, '--root-dir');
  const agentHint = getArgValue(argv, '--agent-hint');

  if (!propertyId || !repoId) {
    console.error('Error: --application and --repo-id are required.');
    process.exit(1);
  }

  const body: Record<string, unknown> = {};
  if (role !== undefined) body.role = role;
  if (rootDir !== undefined) body.rootDir = rootDir;
  if (agentHint !== undefined) body.agentHint = agentHint;

  const result = await apiRequest<{ ok: boolean; id?: string; error?: string }>(
    apiUrl,
    token,
    'PATCH',
    `/org/properties/${propertyId}/github-repos/${repoId}`,
    body
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) console.log(JSON.stringify(result, null, 2));
  else console.log(`Updated repo link ${repoId}`);
}

async function handleRepoUnlink(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = getApplicationArg(argv);
  const repo = getArgValue(argv, '--repo');

  if (!propertyId || !repo) {
    console.error('Error: --application and --repo are required.');
    process.exit(1);
  }

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/properties/${propertyId}/github-repos/${encodeURIComponent(repo)}`
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) console.log(JSON.stringify(result, null, 2));
  else console.log(`Unlinked ${repo} from application ${propertyId}`);
}

async function handleRepo(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;
  switch (action) {
    case 'link':
      await handleRepoLink(rest, apiUrl, token);
      break;
    case 'update':
      await handleRepoUpdate(rest, apiUrl, token);
      break;
    case 'unlink':
      await handleRepoUnlink(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown repo action: ${action ?? '(none)'}`);
      printHelp();
      process.exit(1);
  }
}

export async function runProperty(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  // `skill guide` and `skill help` are doc-only; skip auth resolution so
  // unauthenticated agents can discover the methodology before logging in.
  if (subCommand === 'skill') {
    const skillAction = rest[0];
    if (!skillAction || skillAction === 'help' || skillAction === 'guide') {
      await runPropertySkill(rest, '', '');
      return;
    }
  }

  const { apiUrl, token } = await resolveConfig(argv);

  const globalFlags = new Set(['--env', '--api-url', '--token', '--org']);
  const args = rest.filter((arg, i) => {
    if (globalFlags.has(arg)) return false;
    if (i > 0 && globalFlags.has(rest[i - 1])) return false;
    return true;
  });

  switch (subCommand) {
    case 'list':
      await handleList(args, apiUrl, token);
      break;
    case 'create':
      await handleCreate(args, apiUrl, token);
      break;
    case 'update':
      await handleUpdate(args, apiUrl, token);
      break;
    case 'repo':
      await handleRepo(args, apiUrl, token);
      break;
    case 'skill':
      await runPropertySkill(args, apiUrl, token);
      break;
    case 'page-skill':
      await runPageSkill(args, apiUrl, token);
      break;
    case 'recipe':
      await runRecipe(args, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printHelp();
      process.exit(1);
  }
}
